import PropTypes from 'prop-types';
import bindAll from 'lodash.bindall';
import React from 'react';
import {getEventXY} from '../../lib/touch-utils';

import styles from './dial.css';

import dialFace from './icon--dial.svg';
import dialHandle from './icon--handle.svg';

class Dial extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleMouseDown',
            'handleMouseMove',
            'containerRef',
            'viewportRef',
            'handleRef',
            'unbindMouseEvents',
            'clampDirection'
        ]);
    }

    clampDirection (direction) {
        const {minDirection, maxDirection} = this.props;
        return Math.max(minDirection, Math.min(maxDirection, direction));
    }

    componentDidMount () {
        // Manually add touch/mouse handlers so that preventDefault can be used
        // to prevent scrolling on touch.
        // Tracked as a react issue https://github.com/facebook/react/issues/6436
        this.handleElement.addEventListener('mousedown', this.handleMouseDown);
        this.handleElement.addEventListener('touchstart', this.handleMouseDown);
    }

    componentWillUnmount () {
        this.unbindMouseEvents();
        this.handleElement.removeEventListener('mousedown', this.handleMouseDown);
        this.handleElement.removeEventListener('touchstart', this.handleMouseDown);
    }

    /**
     * Get direction from dial center to mouse move event.
     * @param {Event} e - Mouse move event.
     * @returns {number} Direction in degrees, clockwise, 90=horizontal.
     */
    directionToMouseEvent (e) {
        const {x: mx, y: my} = getEventXY(e);
        const bbox = this.containerElement.getBoundingClientRect();
        const cy = bbox.top + (bbox.height / 2);
        const cx = bbox.left + (bbox.width / 2);
        const angle = Math.atan2(my - cy, mx - cx);
        const degrees = angle * (180 / Math.PI);
        let direction = degrees + 90;
        if (direction > 180) direction -= 360;
        return direction; // To correspond with scratch coordinate system
    }

    /**
     * Create SVG path data string for the dial "gauge", the overlaid arc slice.
     * @param {number} radius - The radius of the dial.
     * @param {number} direction - Direction in degrees, clockwise, 90=horizontal.
     * @returns {string} Path data string for the gauge.
     */
    gaugePath (radius, direction) {
        const rads = (direction) * (Math.PI / 180);
        const path = [];
        path.push(`M ${radius} 0`);
        path.push(`L ${radius} ${radius}`);
        path.push(`L ${radius + (radius * Math.sin(rads))} ${radius - (radius * Math.cos(rads))}`);
        path.push(`A ${radius} ${radius} 0 0 ${direction < 0 ? 1 : 0} ${radius} 0`);
        path.push(`Z`);
        return path.join(' ');
    }

    handleMouseMove (e) {
        this.props.onChange(this.clampDirection(this.directionToMouseEvent(e) + this.directionOffset));
        e.preventDefault();
    }

    unbindMouseEvents () {
        window.removeEventListener('mousemove', this.handleMouseMove);
        window.removeEventListener('mouseup', this.unbindMouseEvents);
        window.removeEventListener('touchmove', this.handleMouseMove);
        window.removeEventListener('touchend', this.unbindMouseEvents);
    }

    handleMouseDown (e) {
        // Because the drag handle is not a single point, there is some initial
        // difference between the current sprite direction and the direction to the mouse
        // Store this offset to prevent jumping when the mouse is moved.
        this.directionOffset = this.clampDirection(this.props.direction) - this.directionToMouseEvent(e);
        window.addEventListener('mousemove', this.handleMouseMove);
        window.addEventListener('mouseup', this.unbindMouseEvents);
        window.addEventListener('touchmove', this.handleMouseMove);
        window.addEventListener('touchend', this.unbindMouseEvents);
        e.preventDefault();
    }

    containerRef (el) {
        this.containerElement = el;
    }

    viewportRef (el) {
        this.viewportElement = el;
    }

    handleRef (el) {
        this.handleElement = el;
    }

    render () {
        const {cropBottom, direction, radius} = this.props;
        const clampedDirection = this.clampDirection(direction);
        return (
            <div className={styles.container}>
                <div
                    className={cropBottom ? styles.dialViewportCrop : undefined}
                    ref={this.viewportRef}
                    style={{
                        width: `${radius * 2}px`,
                        height: cropBottom ? `${radius + 24}px` : `${radius * 2}px`
                    }}
                >
                    <div
                        className={styles.dialContainer}
                        ref={this.containerRef}
                        style={{
                            width: `${radius * 2}px`,
                            height: `${radius * 2}px`
                        }}
                    >
                        <div
                            className={cropBottom ? styles.dialFaceCrop : undefined}
                            style={cropBottom ? {height: `${radius + 1}px`} : undefined}
                        >
                            <img
                                className={styles.dialFace}
                                draggable={false}
                                src={dialFace}
                            />
                            <svg
                                className={styles.gauge}
                                height={radius * 2}
                                width={radius * 2}
                            >
                                <path
                                    className={styles.gaugePath}
                                    d={this.gaugePath(radius, clampedDirection)}
                                />
                            </svg>
                            {cropBottom ? (
                                <svg
                                    className={styles.diameterLine}
                                    height={radius * 2}
                                    width={radius * 2}
                                >
                                    <line
                                        x1="0"
                                        x2={radius * 2}
                                        y1={radius}
                                        y2={radius}
                                    />
                                </svg>
                            ) : null}
                        </div>
                        <img
                            className={styles.dialHandle}
                            draggable={false}
                            ref={this.handleRef}
                            src={dialHandle}
                            style={{
                                top: `${radius - (radius * Math.cos(clampedDirection * (Math.PI / 180)))}px`,
                                left: `${radius + (radius * Math.sin(clampedDirection * (Math.PI / 180)))}px`,
                                transform: `rotate(${clampedDirection}deg)`
                            }}
                        />
                    </div>
                </div>
            </div>
        );
    }
}

Dial.propTypes = {
    cropBottom: PropTypes.bool,
    direction: PropTypes.number,
    maxDirection: PropTypes.number,
    minDirection: PropTypes.number,
    onChange: PropTypes.func.isRequired,
    radius: PropTypes.number
};

Dial.defaultProps = {
    direction: 90, // degrees
    cropBottom: false,
    maxDirection: 180,
    minDirection: -180,
    radius: 56 // px
};

export default Dial;
